import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, CheckCircle2, Circle, ArrowRight, Info, TrendingUp, Bell, BellOff, ExternalLink, BookOpen } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import BrandLogoBar from '@/components/BrandLogoBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A task may include a `exerciseUrl` pointing to a guided-exercise page.
// When set, the row shows a "Faire l'exercice" button that opens the flow directly.
const PROTOCOLS = [
  {
    id: 'band-5-15', label: 'BOLT 5 → 15 s', range: [0, 14.99],
    tasks: [
      { text: 'Mesurez votre BOLT score chaque matin au réveil.', exerciseUrl: '/breathing/practice?mode=challenge&challenge=bolt' },
      { text: 'Respirez par le nez nuit et jour (utiliser le Myotape).' },
      { text: 'Faire des petites apnées répétées (Exercice 1) pendant dix minutes, six fois par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=petites-pauses' },
      { text: 'Faire des apnées en marchant (Exercice 3) : Expirez par le nez puis pincez-vous le nez et marchez cinq à dix pas en apnée. Respirez normalement durant une minute. Répétez dix fois. Faire trois séries par jour.' },
      { text: 'Faire dix à quinze minutes de marche quotidienne en respirant par le nez. Si vous ressentez le besoin de respirer par la bouche, ralentissez ou arrêtez-vous.' },
      { text: 'Dès que votre BOLT score atteint 15 secondes, pratiquez l\u2019exercice de la Respiration Réduite (Exercice 2) une heure par jour divisée en six fois dix minutes.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'A mesure que votre BOLT score augmentera, il vous sera de plus en plus facile de pratiquer des activités sportives. Votre objectif est d\u2019atteindre un BOLT score de vingt-cinq secondes en six à huit semaines.' },
    ],
  },
  {
    id: 'band-15-20', label: 'BOLT 15 → 20 s', range: [15, 19.99],
    tasks: [
      { text: 'Mesurez votre BOLT score chaque matin au réveil.', exerciseUrl: '/breathing/practice?mode=challenge&challenge=bolt' },
      { text: 'Respirez par le nez nuit et jour (utiliser le Myotape de nuit).' },
      { text: 'Observez votre respiration aussi souvent que possible au cours de la journée.' },
      { text: 'Respiration Réduite (Exercice 2) dix minutes deux fois par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'S’échauffer (Exercice 4 ou Exercice 10) pendant dix minutes avant de marcher en faisant des apnées toutes les minutes environ. Assurez-vous de conserver une respiration stable après chaque apnée.', exerciseUrl: '/breathing/practice?mode=guided&exercise=warmup' },
      { text: 'Respiration Réduite en marchant (Exercice 5) trente à soixante minutes par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'Respiration Réduite (Exercice 2) quinze minutes avant d’aller vous coucher.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
    ],
  },
  {
    id: 'band-20-30', label: 'BOLT 20 → 30 s', range: [20, 29.99],
    tasks: [
      { text: 'Mesurez votre BOLT score chaque matin au réveil.', exerciseUrl: '/breathing/practice?mode=challenge&challenge=bolt' },
      { text: 'Respirez par le nez nuit et jour en utilisant Myotape la nuit.' },
      { text: 'Respiration Réduite (Exercice 2) dix minutes deux fois par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'S\u2019échauffer (Exercice 4 ou Exercice 10) pendant dix minutes, avant un exercice physique en marchant en faisant des apnées toutes les minutes environ. Assurez-vous de maintenir une respiration stable après chaque apnée.', exerciseUrl: '/breathing/practice?mode=guided&exercise=warmup' },
      { text: 'Respiration Réduite en marchant à vive allure ou en courant pendant trente à soixante minutes.', exerciseUrl: '/breathing/practice?mode=guided&exercise=breathe-light-walking' },
      { text: 'Simuler un entraînement en Altitude lors de la marche ou de la course en effectuant une série de huit à dix apnées.', exerciseUrl: '/breathing/practice?mode=guided&exercise=altitude-simulation' },
      { text: 'Après chaque entraînement, retrouver une respiration Réduite, Lente et Profonde (LSD) jusqu\u2019à récupération complète de votre effort physique.', exerciseUrl: '/breathing/practice?mode=guided&exercise=recovery' },
      { text: 'Effectuez une session de Simulation d\u2019Entraînement en Altitude - Exercice avancé, une fois par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=altitude-simulation' },
      { text: 'Respiration Réduite (Exercice 2) quinze minutes avant d\u2019aller vous coucher.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
    ],
  },
  {
    id: 'band-30-plus', label: 'BOLT ≥ 30 s', range: [30, 999],
    tasks: [
      { text: 'Mesurez votre BOLT score chaque matin au réveil.', exerciseUrl: '/breathing/practice?mode=challenge&challenge=bolt' },
      { text: 'Respirez par le nez nuit et jour en utilisant Myotape la nuit.' },
      { text: 'Respiration Réduite (Exercice 2) dix minutes deux fois par jour.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'S\u2019échauffer (Exercice 4 ou Exercice 10) pendant dix minutes avant de marcher en faisant des apnées toutes les minutes environ. Assurez-vous de conserver une respiration stable après chaque apnée.', exerciseUrl: '/breathing/practice?mode=guided&exercise=warmup' },
      { text: 'Respiration Réduite durant la course à pied.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
      { text: 'Continuer à courir, adoptez une respiration seulement nasale pendant vingt minutes.' },
      { text: 'En plein milieu d\u2019un entraînement de course à pied effectuez des apnées.' },
      { text: 'Insérez plusieurs apnées à distance de quelques minutes tout au long de votre course.' },
      { text: 'Après chaque entraînement, faites l\u2019exercice de respiration de récupération (Exercice 9).', exerciseUrl: '/breathing/practice?mode=guided&exercise=recovery' },
      { text: 'Effectuez une session de Simulation d\u2019Entraînement en Altitude - Exercice avancé, une fois tous les deux jours.', exerciseUrl: '/breathing/practice?mode=guided&exercise=altitude-simulation' },
      { text: 'Respiration Réduite (Exercice 2) quinze minutes avant d\u2019aller vous coucher.', exerciseUrl: '/breathing/practice?mode=guided&exercise=reduire-frequence' },
    ],
  },
];

const todayKey = () => new Date().toISOString().slice(0, 10);
const storageKey = (protoId) => `oa_tasks_${protoId}_${todayKey()}`;

// Robust date parser — handles ISO strings, epoch numbers, Mongo $date, missing values
const parseChallengeDate = (raw) => {
  if (!raw && raw !== 0) return null;
  if (typeof raw === 'number') return new Date(raw);
  if (typeof raw === 'string') {
    const d = new Date(raw);
    if (!isNaN(d)) return d;
  }
  if (typeof raw === 'object' && raw !== null) {
    if (raw.$date) return parseChallengeDate(raw.$date);
    if (raw.seconds) return new Date(raw.seconds * 1000);
  }
  return null;
};

const OxygenAdvantagePage = () => {
  const navigate = useNavigate();
  const [boltHistory, setBoltHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const [challengesRes, meRes] = await Promise.all([
          axios.get(`${API}/challenges?challenge_type=bolt&limit=500`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        const items = Array.isArray(challengesRes.data) ? challengesRes.data : (challengesRes.data.challenges || []);
        const clean = items
          .map((it) => ({
            value: typeof it.duration_seconds === 'number' ? it.duration_seconds : null,
            date: parseChallengeDate(it.created_at || it.completed_at),
          }))
          .filter((it) => it.value != null && it.value > 0 && it.date && !isNaN(it.date))
          .sort((a, b) => a.date - b.date);
        setBoltHistory(clean);
        if (meRes?.data) setReminderEnabled(!!meRes.data.bolt_reminder_enabled);
      } catch {
        setBoltHistory([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleReminder = async () => {
    if (reminderSaving) return;
    setReminderSaving(true);
    const next = !reminderEnabled;
    setReminderEnabled(next); // optimistic
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/auth/preferences`,
        { bolt_reminder_enabled: next },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      setReminderEnabled(!next); // revert
    } finally {
      setReminderSaving(false);
    }
  };

  const latestBolt = boltHistory.length ? boltHistory[boltHistory.length - 1] : null;

  // Level thresholds: user must hit ≥ threshold at least 7 times in the last 30 days to unlock the next band.
  const LEVEL_THRESHOLDS = [15, 20, 30]; // band-5-15 → band-15-20 → band-20-30 → band-30-plus
  const UNLOCK_COUNT = 7;
  const UNLOCK_WINDOW_DAYS = 30;

  // Compute the unlocked protocol level based on last-30-days counts.
  const levelInfo = useMemo(() => {
    const cutoff = Date.now() - UNLOCK_WINDOW_DAYS * 24 * 3600 * 1000;
    const recent = boltHistory.filter((h) => h.date.getTime() >= cutoff);
    let unlockedIdx = 0;
    for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
      const th = LEVEL_THRESHOLDS[i];
      const count = recent.filter((h) => h.value >= th).length;
      if (count >= UNLOCK_COUNT) unlockedIdx = i + 1;
      else break;
    }
    const protocol = PROTOCOLS[unlockedIdx] || PROTOCOLS[0];
    const nextThreshold = LEVEL_THRESHOLDS[unlockedIdx] ?? null;
    const currentCount = nextThreshold != null
      ? recent.filter((h) => h.value >= nextThreshold).length
      : null;
    return { protocol, unlockedIdx, nextThreshold, currentCount, needed: UNLOCK_COUNT };
  }, [boltHistory]);

  const activeProtocol = latestBolt ? levelInfo.protocol : null;

  // Consecutive-days streak: count calendar days (Europe/local time) ending today
  // or yesterday where at least one BOLT measurement exists.
  const streak = useMemo(() => {
    if (!boltHistory.length) return 0;
    const dayKey = (d) => d.toISOString().slice(0, 10);
    const daysWithBolt = new Set(boltHistory.map((h) => dayKey(h.date)));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Allow the streak to survive today if not yet measured — start from today, else yesterday.
    let cursor = new Date(today);
    if (!daysWithBolt.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
    let count = 0;
    while (daysWithBolt.has(dayKey(cursor))) {
      count += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  }, [boltHistory]);

  useEffect(() => {
    if (!activeProtocol) return;
    try {
      const raw = localStorage.getItem(storageKey(activeProtocol.id));
      setChecked(raw ? JSON.parse(raw) : {});
    } catch { setChecked({}); }
  }, [activeProtocol]);

  const toggle = (idx) => {
    if (!activeProtocol) return;
    const next = { ...checked, [idx]: !checked[idx] };
    setChecked(next);
    try { localStorage.setItem(storageKey(activeProtocol.id), JSON.stringify(next)); } catch { /* ignore */ }
  };

  // Last-30-days chart data (one point per day = latest measurement of the day)
  const chartData = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
    const byDay = new Map();
    for (const h of boltHistory) {
      if (h.date.getTime() < cutoff) continue;
      const key = h.date.toISOString().slice(0, 10);
      // Keep the most recent measurement of the day (boltHistory is sorted asc)
      byDay.set(key, {
        day: h.date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        bolt: Number(h.value.toFixed(1)),
        ts: h.date.getTime(),
      });
    }
    return Array.from(byDay.values()).sort((a, b) => a.ts - b.ts);
  }, [boltHistory]);

  const doneCount = Object.values(checked).filter(Boolean).length;
  const totalCount = activeProtocol?.tasks.length || 0;
  const progress = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  // Next unlock threshold and progression: N/7 measurements >= threshold
  const target = levelInfo.nextThreshold;

  const targetProgress = useMemo(() => {
    if (target == null || !levelInfo.currentCount) return 0;
    return Math.max(0, Math.min(100, (levelInfo.currentCount / UNLOCK_COUNT) * 100));
  }, [target, levelInfo.currentCount]);

  const formatDate = (d) => d ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6" style={{
      background: 'linear-gradient(180deg, #FAF6EE 0%, #F4EEE1 100%)',
      fontFamily: 'Figtree, sans-serif',
    }} data-testid="oxygen-advantage-page">
      <div className="max-w-2xl mx-auto">
        <BrandLogoBar className="mb-4" />
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center px-6 py-4 rounded-2xl mb-4 shadow-md" style={{ background: '#ffffff', border: '1px solid rgba(15,32,68,0.08)' }}>
            <img
              src="/oxygen-advantage-logo.webp"
              alt="Oxygen Advantage"
              className="h-12 sm:h-14 w-auto object-contain"
              data-testid="oa-brand-logo"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f2744' }} data-testid="oa-title">
            Protocole <span className="font-medium" style={{ color: '#B91C1C' }}>Oxygen Advantage</span>
          </h1>
          <a
            href="https://www.oxygenadvantage.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm font-medium underline decoration-slate-300 hover:decoration-red-500 transition"
            style={{ color: '#475569' }}
            data-testid="oa-official-link"
          >
            www.oxygenadvantage.com <ExternalLink size={13} />
          </a>
          <p className="mt-3 text-sm sm:text-base" style={{ color: '#475569' }}>
            Programme quotidien pour l&apos;hyperventilation, adapté à votre BOLT
          </p>
        </div>

        {/* BOLT card */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(15,32,68,0.08)' }}>
          <div className="flex items-start justify-between mb-3 gap-3 flex-col sm:flex-row sm:items-center">
            <div>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>Votre BOLT actuel</p>
              {loading ? (
                <p className="text-sm mt-1" style={{ color: '#64748B' }}>Chargement…</p>
              ) : latestBolt ? (
                <>
                  <p className="text-3xl font-bold mt-1" style={{ color: '#0f2744' }} data-testid="oa-latest-bolt">{latestBolt.value.toFixed(1)} s</p>
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }} data-testid="oa-latest-bolt-date">Mesuré le {formatDate(latestBolt.date)}</p>
                  {streak > 0 && (
                    <div
                      className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-bold"
                      style={{ background: streak >= 7 ? 'linear-gradient(135deg,#F97316,#DC2626)' : 'linear-gradient(135deg,#FCD34D,#F59E0B)', color: '#ffffff', boxShadow: '0 2px 8px rgba(220,38,38,0.25)' }}
                      data-testid="oa-streak-badge"
                      title={`${streak} jour${streak > 1 ? 's' : ''} consécutif${streak > 1 ? 's' : ''} de mesure BOLT`}
                    >
                      <span aria-hidden="true">🔥</span>
                      <span>{streak} jour{streak > 1 ? 's' : ''}</span>
                      {streak >= 30 && <span className="opacity-90 font-medium">· Champion</span>}
                      {streak >= 7 && streak < 30 && <span className="opacity-90 font-medium">· En feu !</span>}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm mt-1" style={{ color: '#64748B' }}>Aucun BOLT enregistré</p>
              )}
            </div>
            <button
              onClick={() => navigate('/breathing/practice?mode=challenge&challenge=bolt')}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white transition hover:scale-105 self-stretch sm:self-auto justify-center"
              style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #0284C7 100%)', boxShadow: '0 6px 20px rgba(2,132,199,0.35)' }}
              data-testid="oa-measure-bolt-btn"
            >
              <Activity size={16} /> Mesurer maintenant
            </button>
          </div>
          {activeProtocol && (
            <div className="mt-2 text-xs" style={{ color: '#64748B' }}>
              Programme actif : <span className="font-semibold" style={{ color: '#0f2744' }}>{activeProtocol.label}</span>
              {levelInfo.nextThreshold != null && (
                <span className="block mt-1" style={{ color: '#94A3B8' }}>
                  Palier suivant à <span className="font-semibold" style={{ color: '#0284C7' }}>{levelInfo.nextThreshold} s</span> :
                  <span className="font-semibold" style={{ color: '#0f2744' }}> {levelInfo.currentCount}/{levelInfo.needed}</span> mesures (30 derniers jours)
                </span>
              )}
            </div>
          )}
          {/* Daily reminder opt-in */}
          <div className="mt-4 pt-4 flex items-start sm:items-center gap-3 flex-col sm:flex-row sm:justify-between" style={{ borderTop: '1px solid rgba(15,32,68,0.08)' }}>
            <div className="flex items-start gap-2">
              {reminderEnabled ? (
                <Bell className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.8} style={{ color: '#0284C7' }} />
              ) : (
                <BellOff className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.8} style={{ color: '#94A3B8' }} />
              )}
              <div className="text-xs leading-relaxed" style={{ color: '#475569' }}>
                <span className="font-medium" style={{ color: '#0f2744' }}>Rappel matinal BOLT</span> — recevez un email à 9h00 pour mesurer votre BOLT du jour.
              </div>
            </div>
            <button
              type="button"
              onClick={toggleReminder}
              disabled={reminderSaving}
              role="switch"
              aria-checked={reminderEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition ${reminderEnabled ? 'bg-sky-500' : 'bg-slate-300'} ${reminderSaving ? 'opacity-60' : ''}`}
              data-testid="oa-reminder-toggle"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition mt-0.5 ${reminderEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}
              />
            </button>
          </div>
          <div className="mt-2 text-right">
            <button
              type="button"
              onClick={() => navigate('/settings/emails')}
              className="text-[11px] underline hover:text-sky-700 transition"
              style={{ color: '#64748B', textDecorationColor: '#CBD5E1' }}
              data-testid="oa-manage-emails"
            >
              Gérer toutes mes préférences email →
            </button>
          </div>
        </div>

        {/* Personalized BOLT target banner */}{latestBolt && target && (
          <div
            className="rounded-2xl p-5 mb-6"
            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(56,189,248,0.06))', border: '1px solid rgba(16,185,129,0.25)', boxShadow: '0 4px 20px rgba(16,185,129,0.06)' }}
            data-testid="oa-target-banner"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#059669' }}>Prochain palier</p>
                <p className="text-lg font-semibold" style={{ color: '#0f2744' }}>
                  Atteindre <span data-testid="oa-target-value" style={{ color: '#059669' }}>{target} s</span> — 7 fois
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px]" style={{ color: '#64748B' }}>Mesures ≥ {target} s (30 j)</p>
                <p className="text-2xl font-bold" style={{ color: '#0f2744' }} data-testid="oa-target-remaining">
                  {levelInfo.currentCount}<span style={{ color: '#94A3B8', fontSize: '1rem' }}> / {UNLOCK_COUNT}</span>
                </p>
              </div>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(15,32,68,0.08)' }}>
              <div
                className="h-full transition-all duration-500 rounded-full"
                style={{ width: `${targetProgress}%`, background: 'linear-gradient(90deg, #34D399, #38BDF8)' }}
                data-testid="oa-target-bar"
              />
            </div>
            <p className="text-xs mt-2" style={{ color: '#64748B' }}>
              {levelInfo.currentCount >= UNLOCK_COUNT
                ? <>Palier atteint ! Le protocole passera au niveau supérieur à votre prochaine mesure.</>
                : <>Encore <span className="font-medium" style={{ color: '#0f2744' }}>{Math.max(0, UNLOCK_COUNT - levelInfo.currentCount)} mesure(s)</span> ≥ {target} s dans les 30 derniers jours pour passer au palier suivant.</>
              }
            </p>
          </div>
        )}
        {latestBolt && !target && (
          <div
            className="rounded-2xl p-5 mb-6 text-center"
            style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.10), rgba(16,185,129,0.10))', border: '1px solid rgba(250,204,21,0.35)' }}
            data-testid="oa-target-banner"
          >
            <p className="font-semibold text-lg" style={{ color: '#B45309' }}>🏆 Niveau Maître atteint</p>
            <p className="text-sm mt-1" style={{ color: '#64748B' }}>Votre BOLT dépasse 40 s — bravo. Maintenez et affinez.</p>
          </div>
        )}

        {/* Evolution chart (last 30 days) */}
        {chartData.length > 1 && (
          <div className="rounded-2xl p-5 mb-6" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(15,32,68,0.08)' }} data-testid="oa-chart">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5" strokeWidth={1.8} style={{ color: '#0284C7' }} />
              <h2 className="text-base font-semibold" style={{ color: '#0f2744' }}>Évolution du BOLT — 30 derniers jours</h2>
            </div>
            <div style={{ width: '100%', height: 200, minWidth: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -18, bottom: 0 }}>
                  <XAxis dataKey="day" stroke="#94A3B8" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} domain={[0, 'dataMax + 5']} />
                  <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: 12, color: '#0f2744', boxShadow: '0 4px 20px rgba(15,32,68,0.1)' }} labelStyle={{ color: '#0284C7' }} formatter={(v) => [`${v} s`, 'BOLT']} />
                  <ReferenceLine y={20} stroke="rgba(16,185,129,0.5)" strokeDasharray="4 4" label={{ value: 'Cible 20s', fill: '#059669', fontSize: 10, position: 'top' }} />
                  <Line type="monotone" dataKey="bolt" stroke="#0284C7" strokeWidth={3} dot={{ r: 4, fill: '#0284C7' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Manuel Oxygen Advantage — accessible only from the user's OA page */}
        <div className="rounded-2xl p-5 mb-6 flex items-center gap-4" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #F0F9FF 100%)', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(2,132,199,0.20)' }}>
          <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0EA5E9,#0284C7)' }}>
            <BookOpen className="w-6 h-6 text-white" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[15px]" style={{ color: '#0f2744' }}>Manuel Oxygen Advantage</p>
            <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>Guide complet des exercices et techniques, adapté à chaque palier BOLT.</p>
          </div>
          <button
            onClick={() => navigate('/view-document?doc=/documents/Manuel_OA_patients.pdf&title=Manuel%20Oxygen%20Advantage')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white transition hover:scale-105 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0EA5E9, #0284C7)', boxShadow: '0 3px 10px rgba(2,132,199,0.25)' }}
            data-testid="oa-open-manual"
          >
            Ouvrir <ArrowRight size={13} />
          </button>
        </div>

        {!activeProtocol ? (
          <div className="rounded-2xl p-8 text-center" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(15,32,68,0.08)', color: '#475569' }}>
            <Info className="w-10 h-10 mx-auto mb-3" strokeWidth={1.4} style={{ color: '#0284C7' }} />
            <p className="mb-2 font-medium" style={{ color: '#0f2744' }}>Mesurez d&apos;abord votre BOLT du jour</p>
            <p className="text-sm" style={{ color: '#64748B' }}>Le programme quotidien s&apos;adapte automatiquement à votre score du matin.</p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl p-5 mb-4" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(15,32,68,0.08)' }}>
              <div className="flex justify-between text-sm mb-2" style={{ color: '#475569' }}>
                <span>Progression du jour</span>
                <span className="font-semibold" style={{ color: '#0f2744' }} data-testid="oa-progress-count">{doneCount}/{totalCount}</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(15,32,68,0.08)' }}>
                <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #34D399, #10B981)' }} data-testid="oa-progress-bar" />
              </div>
            </div>

            <div className="rounded-2xl p-5 sm:p-6" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(15,32,68,0.06)', border: '1px solid rgba(15,32,68,0.08)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f2744' }}>Tâches du jour</h2>
              <ul className="space-y-2" data-testid="oa-task-list">
                {activeProtocol.tasks.map((task, idx) => {
                  const done = !!checked[idx];
                  return (
                    <li key={idx} className="p-3 rounded-xl transition" style={{ background: done ? 'rgba(16,185,129,0.10)' : '#F8F5EE', border: done ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(15,32,68,0.05)' }}>
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggle(idx)}
                          className="flex-shrink-0"
                          data-testid={`oa-task-check-${idx}`}
                          aria-label={done ? 'Décocher' : 'Cocher'}
                        >
                          {done ? (
                            <CheckCircle2 className="w-6 h-6" strokeWidth={2} style={{ color: '#059669' }} />
                          ) : (
                            <Circle className="w-6 h-6" strokeWidth={1.5} style={{ color: '#94A3B8' }} />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-[15px]" style={done ? { color: '#065F46', textDecoration: 'line-through', textDecorationColor: 'rgba(16,185,129,0.5)' } : { color: '#0f2744' }}>{task.text}</p>
                          {task.exerciseUrl && (
                            <button
                              onClick={() => navigate(task.exerciseUrl)}
                              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white transition hover:scale-105"
                              style={{ background: 'linear-gradient(135deg, #38BDF8, #0284C7)', boxShadow: '0 3px 10px rgba(2,132,199,0.25)' }}
                              data-testid={`oa-task-exercise-${idx}`}
                            >
                              Faire l&apos;exercice <ArrowRight size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs mt-4 flex items-center gap-1" style={{ color: '#94A3B8' }}>
                <Info size={12} /> Les cases sont réinitialisées automatiquement chaque jour.
              </p>
            </div>

            <div className="mt-6 text-center">
              <button onClick={() => navigate('/breathing')} className="text-sm inline-flex items-center gap-1 hover:opacity-80 transition" style={{ color: '#64748B' }}>
                Retour au hub des exercices <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OxygenAdvantagePage;
